import { Fragment, useState } from 'react'
import {
    APIQueueTaskStatus,
    type APIChainedQueueTask,
    type APIOrganization,
    type APIQueueTask,
    type APIQueueTaskKind,
    type APIQueueTaskResult,
    isChainedQueueTask,
} from '@visorpro/client'
import { ChevronRight, Warning } from '@mui/icons-material'
import {
    Box,
    LinearProgress,
    Stack,
    type Theme,
    Tooltip,
    Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DateTime } from 'luxon'
import _ from 'lodash'
import { useInterval } from 'react-use'
import { formatDuration } from '../util/format'
import type { DictionaryArray } from '../types'
import { MetadataEntries } from './task-metadata'

/**
 * How a phase of a task went. `waited` and `done` are over, the first in
 * the queue and the second at work. `live` is underway. `failed` and
 * `revoked` are where the task ended. `unreached` never began.
 */
type TimelinePhaseState =
    | 'waited'
    | 'live'
    | 'done'
    | 'failed'
    | 'revoked'
    | 'unreached'

type TimelinePhaseOutcome = 'done' | 'failed' | 'revoked'

/** A stretch of a task's life: its wait in the queue, its run, or a step. */
interface TimelinePhase {
    id: string
    /** Several, in order, when a chain's steps run as one phase. */
    label: string | string[]
    name: string | string[]
    /** The Celery task id of a chain step. */
    taskId?: string
    /** What the phase works on, which the queue has none of. */
    kind?: APIQueueTaskKind
    metadata?: object
    isQueue?: boolean
    startedAt: DateTime | null
    endedAt: DateTime | null
    /** How the phase ended, once it is known to be over. */
    outcome?: TimelinePhaseOutcome
}

interface TimelineSegment extends TimelinePhase {
    state: TimelinePhaseState
    /** Absent when either end of the phase is unknown. */
    seconds?: number
}

// The Celery states of a result that will not change anymore.
const READY_RESULT_STATUSES: readonly string[] = [
    APIQueueTaskStatus.SUCCESS,
    APIQueueTaskStatus.FAILURE,
    APIQueueTaskStatus.REVOKED,
]

const getStepLabel = (kind: APIQueueTaskKind) =>
    _.startCase(kind.replace(/_document$/, ''))

const getChainStepPhases = (task: APIChainedQueueTask): TimelinePhase[] => {
    const steps = task.metadata.chain ?? []
    const results = task.descendants ?? []
    const resultsByTaskId = _.keyBy(results, 'task_id')
    const stepIds = new Set(steps.map((step) => step.id))

    // A step may hand its work to tasks that it spawns, and is only over
    // once they all are. A result belongs to the closest step among the
    // tasks that led to it, itself included.
    const getStepId = (result: APIQueueTaskResult) => {
        const visitedTaskIds = new Set<string>()
        let current: APIQueueTaskResult | undefined = result

        while (current && !visitedTaskIds.has(current.task_id)) {
            if (stepIds.has(current.task_id)) return current.task_id
            visitedTaskIds.add(current.task_id)
            current = current.parent_id
                ? resultsByTaskId[current.parent_id]
                : undefined
        }
    }
    const resultsByStepId = _.groupBy(results, getStepId)

    return steps.map((step) => {
        const result = resultsByTaskId[step.id]
        const stepResults = resultsByStepId[step.id] ?? []
        const statuses = stepResults.map((stepResult) => stepResult.status)
        const isOver =
            result !== undefined &&
            statuses.every((status) => READY_RESULT_STATUSES.includes(status))
        const finishedAts = _.compact(
            stepResults.map((stepResult) => stepResult.finished_at),
        )

        return {
            id: step.id,
            label: getStepLabel(step.kind),
            name: _.startCase(step.kind),
            taskId: step.id,
            kind: step.kind,
            metadata: step.metadata,
            startedAt: result?.started_at ?? null,
            endedAt: isOver
                ? (_.maxBy(finishedAts, (time) => time.toMillis()) ?? null)
                : null,
            outcome: !isOver
                ? undefined
                : statuses.includes(APIQueueTaskStatus.FAILURE)
                  ? 'failed'
                  : statuses.includes(APIQueueTaskStatus.REVOKED)
                    ? 'revoked'
                    : 'done',
        }
    })
}

// Names a chain's run after its steps. The steps' metadata stands for the
// run's when they all share it, as they do when they work on one document.
const getChainRunDescription = (
    task: APIChainedQueueTask,
): Pick<TimelinePhase, 'label' | 'name' | 'kind' | 'metadata'> => {
    const steps = task.metadata.chain ?? []
    const metadatas = _.uniqWith(
        steps.map((step) => step.metadata),
        _.isEqual,
    )

    return {
        label: steps.length
            ? steps.map((step) => getStepLabel(step.kind))
            : 'Run',
        name: steps.length
            ? steps.map((step) => _.startCase(step.kind))
            : 'Run',
        kind: metadatas.length === 1 ? steps[0].kind : undefined,
        metadata: metadatas.length === 1 ? metadatas[0] : undefined,
    }
}

const getTimelinePhases = (task: APIQueueTask): TimelinePhase[] => {
    const queue: TimelinePhase = {
        id: 'queue',
        label: 'Queue',
        name: 'Queue',
        isQueue: true,
        startedAt: task.created_at,
        endedAt: task.started_at,
        outcome: task.started_at ? 'done' : undefined,
    }
    const run: TimelinePhase = {
        id: 'run',
        label: 'Run',
        name: 'Run',
        kind: task.kind,
        metadata: task.metadata,
        startedAt: task.started_at,
        endedAt: task.ended_at,
        outcome:
            task.status === APIQueueTaskStatus.SUCCESS ? 'done' : undefined,
    }

    if (isChainedQueueTask(task)) {
        // The results of the steps come with a task only when it is
        // fetched on its own. Without them, the chain runs as one phase.
        if (task.descendants === undefined) {
            return [queue, { ...run, ...getChainRunDescription(task) }]
        }
        return [queue, ...getChainStepPhases(task)]
    }

    return [queue, run]
}

const getTimelineStatus = (task: APIQueueTask): string => {
    // A step has no result until it is published, which happens once the
    // step before it is over. In between, every result that the chain has
    // is settled and the pipeline reports the chain as successful, while
    // there are steps still to come. Only a chain fetched on its own has
    // the results to tell.
    if (
        isChainedQueueTask(task) &&
        task.status === APIQueueTaskStatus.SUCCESS &&
        task.descendants !== undefined
    ) {
        const resultsByTaskId = _.keyBy(task.descendants ?? [], 'task_id')
        const steps = task.metadata.chain ?? []

        if (steps.some((step) => !resultsByTaskId[step.id])) {
            return APIQueueTaskStatus.STARTED
        }
    }

    return task.status
}

const getTimelineSegments = (
    task: APIQueueTask,
    now: DateTime,
): TimelineSegment[] => {
    const phases = getTimelinePhases(task)
    const status = getTimelineStatus(task)
    // The phase that the task is in, or was in when it ended.
    const currentIndex = phases.findIndex((phase) => phase.outcome !== 'done')
    const segments: TimelineSegment[] = []
    let previousEndedAt: DateTime | null = null

    phases.forEach((phase, index) => {
        const outcome =
            phase.outcome ??
            (status === APIQueueTaskStatus.SUCCESS ? 'done' : undefined)
        const state: TimelinePhaseState =
            outcome === 'done'
                ? phase.isQueue
                    ? 'waited'
                    : 'done'
                : (outcome ??
                  (index !== currentIndex
                      ? 'unreached'
                      : status === APIQueueTaskStatus.FAILURE
                        ? 'failed'
                        : status === APIQueueTaskStatus.REVOKED
                          ? 'revoked'
                          : 'live'))

        const isUnderway = state === 'live'
        const isReached = state !== 'unreached'
        // A phase with no start of its own, such as a step that waits for
        // a worker, begins where the phase before it ended.
        const startedAt = isReached
            ? (phase.startedAt ?? previousEndedAt)
            : null
        const endedAt =
            isReached && !isUnderway
                ? (phase.endedAt ??
                  (index === currentIndex ? task.ended_at : null))
                : null
        const end = isUnderway ? now : endedAt

        segments.push({
            ...phase,
            state,
            startedAt,
            endedAt,
            seconds:
                startedAt && end
                    ? // The clocks of the browser and of the pipeline may
                      // disagree by a moment.
                      Math.max(0, end.diff(startedAt).as('seconds'))
                    : undefined,
        })
        previousEndedAt = endedAt
    })

    return segments
}

// The time of day, along with its date unless it falls on the same day as
// the reference.
const formatTimelineTime = (time: DateTime, reference?: DateTime) => {
    const localTime = time.setZone()

    if (reference && localTime.hasSame(reference.setZone(), 'day')) {
        return localTime.toFormat('h:mm:ss a')
    }
    if (localTime.hasSame(DateTime.local(), 'year')) {
        return localTime.toFormat('MMM d, h:mm:ss a')
    }
    return localTime.toFormat('MMM d, y, h:mm:ss a')
}

// Renders the component again every second while ticking, so that the
// duration of a live phase keeps counting between two polls.
const useTick = (isTicking: boolean) => {
    const [, setTick] = useState(0)
    useInterval(() => setTick((tick) => tick + 1), isTicking ? 1000 : null)
}

interface TaskTimelineProps {
    task: APIQueueTask
    /** Names the organizations that the tasks' metadata refers to. */
    organizations?: DictionaryArray<APIOrganization>
}

/**
 * The phases that a task went through, each with how long it took, above
 * when the task was enqueued and when it ended. The tooltip of a phase
 * tells what it works on.
 */
export const TaskTimeline = ({ task, organizations }: TaskTimelineProps) => {
    // The pipeline holds no result for an ignored task, so there is no
    // phase to tell apart.
    const isIgnored = task.status === APIQueueTaskStatus.IGNORED
    const segments: TimelineSegment[] = isIgnored
        ? [
              {
                  id: 'ignored',
                  label: 'No run recorded',
                  name: 'No run recorded',
                  state: 'unreached',
                  startedAt: null,
                  endedAt: null,
                  // The metadata of a chain is its steps, none of which
                  // is shown for an ignored one.
                  ...(!isChainedQueueTask(task) && {
                      kind: task.kind,
                      metadata: task.metadata,
                  }),
              },
          ]
        : getTimelineSegments(task, DateTime.now())

    useTick(segments.some((segment) => segment.state === 'live'))

    const status = getTimelineStatus(task)
    const endedAt = READY_RESULT_STATUSES.includes(status)
        ? task.ended_at
        : null

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',
                rowGap: 0.5,
                minWidth: 0,
                width: '100%',
                py: 0.5,
                fontVariantNumeric: 'tabular-nums',
            }}>
            <Stack direction="row" spacing={0.5}>
                {segments.map((segment) => (
                    <TimelineSegmentBar
                        key={segment.id}
                        segment={segment}
                        enqueuedAt={task.created_at}
                        organizations={organizations}
                    />
                ))}
            </Stack>
            {/* The times sit under the two ends of the timeline. In a
                column too narrow for both, the end time goes below the
                enqueue time rather than being cut. */}
            <Typography
                variant="caption"
                component="div"
                color="text.secondary"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 1,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                }}>
                <span>{formatTimelineTime(task.created_at)}</span>
                {endedAt && (
                    <Box
                        component="span"
                        sx={{
                            ml: 'auto',
                            color:
                                status === APIQueueTaskStatus.FAILURE
                                    ? 'error.light'
                                    : undefined,
                        }}>
                        {formatTimelineTime(endedAt, task.created_at)}
                    </Box>
                )}
            </Typography>
        </Box>
    )
}

// The parts of a label or a name in order, a chevron between each two.
const PhaseText = ({ text }: { text: string | string[] }) =>
    typeof text === 'string'
        ? text
        : text.map((part, index) => (
              <Fragment key={index}>
                  {index > 0 && (
                      <ChevronRight
                          sx={{ fontSize: '1.2em', verticalAlign: 'middle' }}
                      />
                  )}
                  {part}
              </Fragment>
          ))

// Wide enough for the name of the queue and a short duration, such as
// "Queue 45s" or "Queue 10m".
const QUEUE_SEGMENT_WIDTH = 72

// The largest unit of a duration alone, such as "10m" for 10m 14s.
const formatShortDuration = (secondsTotal: number) => {
    const seconds = Math.floor(secondsTotal)

    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
    return `${Math.floor(seconds / 86400)}d`
}

interface TimelineSegmentBarProps {
    segment: TimelineSegment
    enqueuedAt: DateTime
    organizations?: DictionaryArray<APIOrganization>
}

const TimelineSegmentBar = ({
    segment,
    enqueuedAt,
    organizations,
}: TimelineSegmentBarProps) => {
    const { state, startedAt, endedAt, seconds, kind, metadata } = segment
    const isFailed = state === 'failed'

    const times =
        state === 'unreached'
            ? 'Not started'
            : state === 'live'
              ? startedAt
                  ? `Since ${formatTimelineTime(startedAt, enqueuedAt)}`
                  : 'Underway'
              : startedAt && endedAt
                ? `${formatTimelineTime(startedAt, enqueuedAt)} → ${formatTimelineTime(endedAt, enqueuedAt)}`
                : endedAt
                  ? `Ended ${formatTimelineTime(endedAt, enqueuedAt)}`
                  : undefined

    return (
        <Tooltip
            placement="top-start"
            // An id of the metadata is wider than a tooltip is by default.
            slotProps={{ tooltip: { sx: { maxWidth: 400 } } }}
            title={
                <>
                    <Box sx={{ fontWeight: 500 }}>
                        <PhaseText text={segment.name} />
                    </Box>
                    {times && <Box>{times}</Box>}
                    {seconds !== undefined && (
                        <Box>{formatDuration(seconds)}</Box>
                    )}
                    {segment.taskId && (
                        <Box sx={{ fontFamily: 'monospace' }}>
                            {segment.taskId}
                        </Box>
                    )}
                    {kind && metadata && !_.isEmpty(metadata) && (
                        <Box sx={{ mt: 0.5 }}>
                            <MetadataEntries
                                kind={kind}
                                metadata={metadata}
                                organizations={organizations}
                            />
                        </Box>
                    )}
                </>
            }>
            <Box
                sx={{
                    // Widths do not stand for durations: a wait of seconds
                    // would vanish next to a run of minutes. The queue is
                    // as wide in every row, so that the phases after it
                    // start at the same place down the column.
                    flex: segment.isQueue
                        ? `0 0 ${QUEUE_SEGMENT_WIDTH}px`
                        : '2 1 auto',
                    minWidth: 24,
                    display: 'grid',
                    rowGap: 0.5,
                }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ minWidth: 0, pr: segment.isQueue ? 0 : 1 }}>
                    {/* The queue has no room for the icon next to its
                        name and its duration. Its label and its bar tell
                        how it ended. */}
                    {(isFailed || state === 'revoked') && !segment.isQueue && (
                        <Warning
                            sx={{
                                fontSize: 14,
                                color: isFailed ? 'error.light' : 'grey',
                            }}
                        />
                    )}
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            lineHeight: '16px',
                            color: isFailed
                                ? 'error.light'
                                : state === 'live'
                                  ? 'primary.light'
                                  : state === 'unreached'
                                    ? 'text.disabled'
                                    : 'text.secondary',
                        }}>
                        <PhaseText text={segment.label} />
                    </Typography>
                    {seconds !== undefined && (
                        <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{
                                flexShrink: 0,
                                lineHeight: '16px',
                                color: isFailed ? 'error.light' : undefined,
                            }}>
                            {/* The full duration is in the tooltip. */}
                            {segment.isQueue
                                ? formatShortDuration(seconds)
                                : formatDuration(seconds)}
                        </Typography>
                    )}
                </Stack>
                {state === 'live' ? (
                    <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
                ) : (
                    <Box
                        sx={[
                            { height: 6, borderRadius: 3 },
                            TIMELINE_BAR_STYLES[state],
                        ]}
                    />
                )}
            </Box>
        </Tooltip>
    )
}

const TIMELINE_BAR_STYLES = {
    waited: { bgcolor: 'grey.400', opacity: 0.6 },
    done: { bgcolor: 'success.light' },
    failed: { bgcolor: 'error.light' },
    revoked: (theme: Theme) => ({
        backgroundImage: `repeating-linear-gradient(135deg, ${
            theme.palette.grey[400]
        } 0 3px, ${alpha(theme.palette.grey[400], 0.25)} 3px 6px)`,
    }),
    unreached: { border: '1px dashed', borderColor: 'grey.500' },
}
